import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties, SyntheticEvent } from 'react';

import { useLanguage } from '../lib/i18n/LanguageContext';

interface PlaneSlot {
  id: number;
  bannerIndex: number;
  y: number;
  scale: number;
  opacity: number;
  duration: number;
  delay: number;
  driftY: number;
  rightToLeft: boolean;
}

interface RopeCoords {
  tailX: number;
  tailY: number;
  bannerLeftX: number;
  bannerRightX: number;
  bannerY: number;
}

function rand(min: number, max: number) {
  return min + Math.random() * (max - min);
}

function pickBannerIndex(poolSize: number, exclude: number[] = []) {
  if (poolSize <= 0) return 0;
  if (poolSize === 1) return 0;

  const blocked = new Set(exclude);
  const available = Array.from({ length: poolSize }, (_, index) => index).filter(
    (index) => !blocked.has(index),
  );
  if (available.length === 0) {
    return Math.floor(Math.random() * poolSize);
  }
  return available[Math.floor(Math.random() * available.length)]!;
}

function createSlot(id: number, bannerIndex: number): PlaneSlot {
  const lanes: [number, number] = [10, 19];
  return {
    id,
    bannerIndex,
    y: lanes[id] ?? lanes[0],
    scale: rand(0.72, 0.86),
    opacity: rand(0.62, 0.82),
    duration: rand(40, 54),
    delay: id === 0 ? rand(2, 8) : rand(24, 34),
    driftY: rand(-6, 6),
    rightToLeft: id % 2 === 1,
  };
}

function createInitialSlots(bannerCount: number): PlaneSlot[] {
  const firstIndex = pickBannerIndex(bannerCount);
  const secondIndex = pickBannerIndex(bannerCount, [firstIndex]);
  return [createSlot(0, firstIndex), createSlot(1, secondIndex)];
}

function PlaneIcon() {
  return (
    <img
      className="auth-sky-plane-icon"
      src="/auth-plane.png"
      alt=""
      draggable={false}
    />
  );
}

function TowRopes({ coords }: { coords: RopeCoords | null }) {
  if (!coords) return null;

  return (
    <svg
      className="auth-sky-plane-ropes"
      viewBox="0 0 100 100"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      preserveAspectRatio="none"
    >
      <line
        x1={coords.tailX}
        y1={coords.tailY}
        x2={coords.bannerLeftX}
        y2={coords.bannerY}
        stroke="currentColor"
        strokeWidth="0.55"
        vectorEffect="non-scaling-stroke"
        strokeLinecap="round"
      />
      <line
        x1={coords.tailX}
        y1={coords.tailY}
        x2={coords.bannerRightX}
        y2={coords.bannerY}
        stroke="currentColor"
        strokeWidth="0.55"
        vectorEffect="non-scaling-stroke"
        strokeLinecap="round"
      />
    </svg>
  );
}

function measureRopes(root: HTMLElement, rightToLeft: boolean): RopeCoords | null {
  const lead = root.querySelector<HTMLElement>('.auth-sky-plane-lead');
  const banner = root.querySelector<HTMLElement>('.auth-sky-plane-banner');
  if (!lead || !banner) return null;

  const rootRect = root.getBoundingClientRect();
  const width = rootRect.width;
  const height = rootRect.height;
  if (width <= 0 || height <= 0) return null;

  const pctX = (value: number) => (value / width) * 100;
  const pctY = (value: number) => (value / height) * 100;

  const leadRect = lead.getBoundingClientRect();
  const bannerRect = banner.getBoundingClientRect();

  const tailX = rightToLeft
    ? pctX(leadRect.right - rootRect.left - leadRect.width * 0.12)
    : pctX(leadRect.left - rootRect.left + leadRect.width * 0.12);
  const tailY = pctY(leadRect.top - rootRect.top + leadRect.height * 0.58);
  const bannerLeftX = pctX(bannerRect.left - rootRect.left + 8);
  const bannerRightX = pctX(bannerRect.right - rootRect.left - 8);
  const bannerY = pctY(bannerRect.top - rootRect.top + 3);

  return { tailX, tailY, bannerLeftX, bannerRightX, bannerY };
}

function PlaneSlotView({
  slot,
  bannerText,
  onCycle,
}: {
  slot: PlaneSlot;
  bannerText: string;
  onCycle: (slotId: number) => void;
}) {
  const rootRef = useRef<HTMLSpanElement>(null);
  const [coords, setCoords] = useState<RopeCoords | null>(null);

  const updateRopes = useCallback(() => {
    const root = rootRef.current;
    if (!root) return;
    setCoords(measureRopes(root, slot.rightToLeft));
  }, [slot.rightToLeft]);

  useLayoutEffect(() => {
    updateRopes();
  }, [updateRopes, bannerText, slot.scale]);

  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root || typeof ResizeObserver === 'undefined') return;

    const observer = new ResizeObserver(updateRopes);
    observer.observe(root);
    return () => observer.disconnect();
  }, [updateRopes]);

  return (
    <span
      ref={rootRef}
      className={`auth-sky-plane${slot.rightToLeft ? ' auth-sky-plane--rtl' : ' auth-sky-plane--ltr'}`}
      style={
        {
          top: `${slot.y}%`,
          ['--plane-scale' as string]: slot.scale,
          ['--plane-opacity' as string]: slot.opacity,
          ['--plane-duration' as string]: `${slot.duration}s`,
          ['--plane-delay' as string]: `${slot.delay}s`,
          ['--plane-drift-y' as string]: `${slot.driftY}px`,
        } as CSSProperties
      }
      onAnimationIteration={(event: SyntheticEvent<HTMLSpanElement>) => {
        if (event.currentTarget === event.target) {
          onCycle(slot.id);
        }
      }}
    >
      <TowRopes coords={coords} />
      <span className="auth-sky-plane-lead">
        <PlaneIcon />
      </span>
      <span className="auth-sky-plane-tow">
        <span className="auth-sky-plane-banner">{bannerText}</span>
      </span>
    </span>
  );
}

/** Two planes max — towed banners cycle through a larger message pool. */
export function AuthSkyPlanes() {
  const { dict } = useLanguage();
  const banners = dict.authSky.planeBanners;

  const initialSlots = useMemo(
    () => createInitialSlots(banners.length),
    [banners.length],
  );

  const [slots, setSlots] = useState(initialSlots);

  const handleCycle = useCallback(
    (slotId: number) => {
      setSlots((current) => {
        const otherBannerIndex = current.find((slot) => slot.id !== slotId)?.bannerIndex;
        return current.map((slot) => {
          if (slot.id !== slotId) return slot;
          return {
            ...slot,
            bannerIndex: pickBannerIndex(
              banners.length,
              otherBannerIndex === undefined ? [slot.bannerIndex] : [slot.bannerIndex, otherBannerIndex],
            ),
            scale: rand(0.72, 0.86),
            opacity: rand(0.62, 0.82),
            driftY: rand(-6, 6),
            rightToLeft: !slot.rightToLeft,
          };
        });
      });
    },
    [banners.length],
  );

  return (
    <div className="auth-sky-planes" aria-hidden="true">
      {slots.map((slot) => (
        <PlaneSlotView
          key={slot.id}
          slot={slot}
          bannerText={banners[slot.bannerIndex] ?? ''}
          onCycle={handleCycle}
        />
      ))}
    </div>
  );
}
